import React, { useState, useRef, useEffect } from 'react';
import { FunnelIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import PricingFilter from './PricingFilter';

interface FloatingFilterWrapperProps {
    className?: string;
}

const FloatingFilterWrapper: React.FC<FloatingFilterWrapperProps> = ({ className }) => {
    const { actualTheme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className={`relative z-30 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 ${className || ''}`} ref={wrapperRef}>

            {/* Centered Toggle Button */}
            <div className="flex justify-center">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`
            group flex items-center gap-3 px-8 py-4 rounded-full shadow-lg 
            transition-all duration-300 hover:scale-105 active:scale-95
            ${isOpen ? 'translate-y-1' : ''}
            ${actualTheme === 'dark'
                            ? 'bg-primary-600 text-white hover:bg-primary-500 shadow-primary-900/30'
                            : 'bg-white text-primary-900 hover:bg-neutral-50 border border-neutral-200'
                        }
          `}
                >
                    <FunnelIcon className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    <span className="font-bold text-lg tracking-wide">
                        {isOpen ? 'Close Filters' : 'Filter Safaris'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Collapsible Card Container */}
            <div
                className={`
          absolute left-0 right-0 top-full mt-4 mx-4 sm:mx-6 lg:mx-8
          transition-all duration-500 ease-out origin-top
          ${isOpen
                        ? 'opacity-100 scale-100 translate-y-0 visible z-50'
                        : 'opacity-0 scale-95 -translate-y-4 invisible pointer-events-none z-[-1]'
                    }
        `}
            >
                <div className={`
          rounded-3xl shadow-2xl overflow-hidden
          ${actualTheme === 'dark'
                        ? 'bg-slate-900 border border-slate-700 shadow-slate-900/50'
                        : 'bg-white border border-neutral-200 shadow-neutral-200/50'
                    }
        `}>
                    {/* Header */}
                    <div className={`
            px-6 py-4 flex items-center justify-between border-b
            ${actualTheme === 'dark' ? 'border-slate-800' : 'border-neutral-100'}
          `}>
                        <div className="flex items-center gap-3">
                            <div className={`
                p-2 rounded-xl
                ${actualTheme === 'dark' ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-600'}
              `}>
                                <FunnelIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${actualTheme === 'dark' ? 'text-white' : 'text-neutral-900'}`}>
                                    Filter Your Experience
                                </h3>
                                <p className={`text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                                    Find the perfect safari for you
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className={`
                p-2 rounded-full transition-colors
                ${actualTheme === 'dark'
                                    ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                                    : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-900'
                                }
              `}
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Filter Content */}
                    <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                        <PricingFilter />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FloatingFilterWrapper;
